/* ==========================================================================
   iBuyXi Nova — filtering, sorting & paging without page reloads
   Uses Shopify's Section Rendering API; every state is a real, shareable URL.
   ========================================================================== */
(() => {
  if (window.IBX?.facetsReady) return;
  const IBX = window.IBX;
  IBX.facetsReady = true;
  const cache = new Map();

  const root = () => document.querySelector('[data-facets-root]');
  const sectionId = () => root()?.dataset.sectionId;

  function formToUrl(form) {
    const fd = new FormData(form);
    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) { if (v !== '' && v !== null) params.append(k, v); }
    const base = form.getAttribute('action') || location.pathname;
    const qs = params.toString();
    return base + (qs ? `?${qs}` : '');
  }

  async function render(url, { push = true, scroll = false } = {}) {
    const r = root();
    if (!r) { location.href = url; return; }
    r.classList.add('is-loading');
    const u = new URL(url, location.origin);
    u.searchParams.delete('section_id');
    const fetchUrl = new URL(u);
    fetchUrl.searchParams.set('section_id', sectionId());
    try {
      let html = cache.get(fetchUrl.toString());
      if (!html) {
        const res = await fetch(fetchUrl.toString());
        if (!res.ok) throw new Error(res.status);
        html = await res.text();
        cache.set(fetchUrl.toString(), html);
      }
      const doc = IBX.parse(html);
      const swap = (sel) => { const fresh = doc.querySelector(sel); const cur = r.querySelector(sel); if (fresh && cur) cur.innerHTML = fresh.innerHTML; };
      swap('[data-facets-results]');
      swap('[data-facets-toolbar]');
      /* Keep open/closed state of filter groups and the drawer itself */
      const panel = r.querySelector('[data-facets-panel]');
      const freshPanel = doc.querySelector('[data-facets-panel]');
      if (panel && freshPanel) {
        const open = new Set([...panel.querySelectorAll('details[open]')].map((d) => d.dataset.facet));
        const focusedName = document.activeElement?.name;
        panel.innerHTML = freshPanel.innerHTML;
        panel.querySelectorAll('details[data-facet]').forEach((d) => { if (open.has(d.dataset.facet)) d.open = true; });
        if (focusedName) panel.querySelector(`[name="${CSS.escape(focusedName)}"]`)?.focus({ preventScroll: true });
      }
      if (push) history.pushState({ facets: true }, '', u.pathname + u.search);
      bindDensity();
      IBX.wishlist?.paint();
      setupInfinite();
      if (scroll) r.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.dispatchEvent(new CustomEvent('facets:rendered'));
    } catch (e) {
      location.href = u.toString();
    } finally {
      r.classList.remove('is-loading');
    }
  }

  const onChange = IBX.debounce((form) => render(formToUrl(form)), 350);

  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (form && !e.target.matches('[data-facet-search], [data-range-min], [data-range-max]')) { onChange(form); return; }
    const sort = e.target.closest('[data-facets-sort]');
    if (sort) {
      const f = document.querySelector('[data-facets-form]');
      if (f) { f.querySelector('[data-facets-sort-input]').value = sort.value; render(formToUrl(f)); }
      else { const u = new URL(location.href); u.searchParams.set('sort_by', sort.value); render(u.toString()); }
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (!form) return;
    e.preventDefault();
    render(formToUrl(form));
  });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-facets-link]');
    if (link && root()?.contains(link)) {
      e.preventDefault();
      render(link.href, { scroll: !!link.closest('.pagination') });
      return;
    }
    const more = e.target.closest('[data-facet-more]');
    if (more) {
      const list = more.previousElementSibling;
      const expanded = list.classList.toggle('is-expanded');
      if (!more.dataset.more) more.dataset.more = more.textContent;
      more.textContent = expanded ? more.dataset.less : more.dataset.more;
      return;
    }
    const lm = e.target.closest('[data-load-more]');
    if (lm) { e.preventDefault(); loadMore(lm); }
  });

  /* Search inside long filter lists (brands, colours, sizes) */
  document.addEventListener('input', (e) => {
    const s = e.target.closest('[data-facet-search]');
    if (!s) return;
    const term = s.value.trim().toLowerCase();
    const list = s.nextElementSibling;
    list.classList.toggle('is-expanded', !!term);
    list.querySelectorAll('[data-facet-value]').forEach((li) => { li.hidden = term && !li.dataset.facetValue.includes(term); });
  });

  window.addEventListener('popstate', () => { if (root()) render(location.href, { push: false }); });

  /* ---------- Load more / infinite scroll ---------- */
  async function loadMore(btn) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    const u = new URL(btn.href, location.origin);
    u.searchParams.set('section_id', sectionId());
    try {
      const doc = IBX.parse(await (await fetch(u)).text());
      const grid = root().querySelector('[data-product-grid]');
      doc.querySelectorAll('[data-product-grid] > .grid__cell').forEach((cell) => grid.appendChild(cell));
      const freshPaging = doc.querySelector('[data-paging]');
      const paging = root().querySelector('[data-paging]');
      if (freshPaging && paging) paging.innerHTML = freshPaging.innerHTML; else paging?.remove();
      IBX.wishlist?.paint();
      setupInfinite();
    } catch (e) { location.href = btn.href; }
  }

  let io;
  function setupInfinite() {
    const r = root();
    if (!r || r.dataset.paging !== 'infinite' || !('IntersectionObserver' in window)) return;
    io?.disconnect();
    const btn = r.querySelector('[data-load-more]');
    if (!btn) return;
    io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) loadMore(btn); }, { rootMargin: '800px 0px' });
    io.observe(btn);
  }

  /* ---------- Grid density ---------- */
  function bindDensity() {
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    const saved = IBX.store.get('ibx:density');
    if (saved && window.matchMedia('(min-width: 990px)').matches) grid.style.setProperty('--cols', saved);
    document.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(String(saved || '') === b.dataset.density)));
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-density]');
    if (!b) return;
    IBX.store.set('ibx:density', b.dataset.density);
    bindDensity();
  });

  /* ---------- <price-range> dual slider synced with inputs ---------- */
  if (!customElements.get('price-range')) {
    customElements.define('price-range', class extends HTMLElement {
      connectedCallback() {
        const [rMin, rMax] = [this.querySelector('[data-range-min]'), this.querySelector('[data-range-max]')];
        const [iMin, iMax] = [this.querySelector('[data-price-min]'), this.querySelector('[data-price-max]')];
        const max = Number(this.dataset.max);
        const paint = () => {
          const a = Number(rMin.value), b = Number(rMax.value);
          this.style.setProperty('--a', (a / max) * 100 + '%');
          this.style.setProperty('--b', (b / max) * 100 + '%');
        };
        const fire = IBX.debounce(() => iMax.dispatchEvent(new Event('change', { bubbles: true })), 450);
        rMin.addEventListener('input', () => { if (+rMin.value > +rMax.value) rMin.value = rMax.value; iMin.value = +rMin.value > 0 ? rMin.value : ''; paint(); fire(); });
        rMax.addEventListener('input', () => { if (+rMax.value < +rMin.value) rMax.value = rMin.value; iMax.value = +rMax.value < max ? rMax.value : ''; paint(); fire(); });
        iMin.addEventListener('input', () => { rMin.value = iMin.value || 0; paint(); });
        iMax.addEventListener('input', () => { rMax.value = iMax.value || max; paint(); });
        paint();
      }
    });
  }

  /* ---------- Desktop sidebar: the filter drawer is always visible there, so it must not be aria-hidden ---------- */
  const sidebarMq = window.matchMedia('(min-width: 990px)');
  function syncSidebar() {
    document.querySelectorAll('.collection__layout--sidebar .facets-drawer').forEach((d) => {
      if (d.classList.contains('is-open')) return;
      if (sidebarMq.matches) d.removeAttribute('aria-hidden'); else d.setAttribute('aria-hidden', 'true');
    });
  }
  sidebarMq.addEventListener('change', syncSidebar);
  document.addEventListener('drawer:close', syncSidebar);

  document.addEventListener('DOMContentLoaded', () => { bindDensity(); setupInfinite(); syncSidebar(); });
  bindDensity();
  setupInfinite();
  syncSidebar();
})();
